import { useState } from "react";
import axios from "axios";
import { ChevronRight, Mail, Phone, MapPin } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { API } from "@/lib/api";

const EMPTY_CONTACT = { name: "", email: "", phone: "", message: "" };
const EMPTY_SIGNUP = { name: "", email: "", phone: "", user_type: "" };

export const Contact = () => {
  const [contactForm, setContactForm] = useState(EMPTY_CONTACT);
  const [signupForm, setSignupForm] = useState(EMPTY_SIGNUP);
  const [isContactSubmitting, setIsContactSubmitting] = useState(false);
  const [isSignupSubmitting, setIsSignupSubmitting] = useState(false);

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setIsContactSubmitting(true);
    try {
      await axios.post(`${API}/contact`, contactForm);
      toast.success("Message sent successfully! We'll get back to you soon.");
      setContactForm(EMPTY_CONTACT);
    } catch {
      toast.error("Failed to send message. Please try again.");
    }
    setIsContactSubmitting(false);
  };

  const handleSignupSubmit = async (e) => {
    e.preventDefault();
    setIsSignupSubmitting(true);
    try {
      await axios.post(`${API}/prelaunch`, signupForm);
      toast.success("Welcome to KidneyCheQ! You're on the pre-launch list.");
      setSignupForm(EMPTY_SIGNUP);
    } catch (error) {
      if (error.response?.data?.detail === "Email already registered for pre-launch") {
        toast.error("This email is already registered for pre-launch.");
      } else {
        toast.error("Failed to sign up. Please try again.");
      }
    }
    setIsSignupSubmitting(false);
  };

  return (
    <section id="contact" data-testid="contact-section" className="relative py-24 md:py-32">
      <div className="absolute inset-0 bg-gradient-to-b from-white via-slate-50 to-white" />

      <div className="relative max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Pre-Launch Signup */}
          <div id="signup" data-testid="signup-section" className="bg-white rounded-2xl p-6 sm:p-8 md:p-10 border border-cyan-200 shadow-lg">
            <div className="flex items-center gap-3 mb-6">
              <img
                src="https://customer-assets.emergentagent.com/job_ominar-hub/artifacts/rbjuvupr_Orange%20logo%20horizontal%20%281%29.png"
                alt="KidneyCheQ"
                className="w-12 h-12 object-contain"
              />
              <div>
                <h3 className="font-display text-2xl font-bold text-slate-800">Join Pre-Launch</h3>
                <p className="text-slate-600 text-sm">Be the first to get KidneyCheQ</p>
              </div>
            </div>

            <form onSubmit={handleSignupSubmit} className="space-y-5">
              <div>
                <Label htmlFor="signup-name" className="text-slate-700">Full Name *</Label>
                <Input
                  id="signup-name"
                  data-testid="signup-name-input"
                  type="text"
                  required
                  value={signupForm.name}
                  onChange={(e) => setSignupForm({ ...signupForm, name: e.target.value })}
                  className="mt-2 bg-slate-100/50 border-slate-300 text-slate-800 focus:border-cyan-500"
                  placeholder="Your full name"
                />
              </div>

              <div>
                <Label htmlFor="signup-email" className="text-slate-700">Email Address *</Label>
                <Input
                  id="signup-email"
                  data-testid="signup-email-input"
                  type="email"
                  required
                  value={signupForm.email}
                  onChange={(e) => setSignupForm({ ...signupForm, email: e.target.value })}
                  className="mt-2 bg-slate-100/50 border-slate-300 text-slate-800 focus:border-cyan-500"
                  placeholder="your@email.com"
                />
              </div>

              <div>
                <Label htmlFor="signup-phone" className="text-slate-700">Phone Number</Label>
                <Input
                  id="signup-phone"
                  data-testid="signup-phone-input"
                  type="tel"
                  value={signupForm.phone}
                  onChange={(e) => setSignupForm({ ...signupForm, phone: e.target.value })}
                  className="mt-2 bg-slate-100/50 border-slate-300 text-slate-800 focus:border-cyan-500"
                  placeholder="+91 XXXXX XXXXX"
                />
              </div>

              <div>
                <Label htmlFor="signup-type" className="text-slate-700">I am a *</Label>
                <Select
                  value={signupForm.user_type}
                  onValueChange={(value) => setSignupForm({ ...signupForm, user_type: value })}
                  required
                >
                  <SelectTrigger
                    data-testid="signup-type-select"
                    className="mt-2 bg-slate-100/50 border-slate-300 text-slate-800 focus:border-cyan-500"
                  >
                    <SelectValue placeholder="Select your role" />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-100 border-slate-300">
                    <SelectItem value="patient">Patient / Individual</SelectItem>
                    <SelectItem value="healthcare_provider">Healthcare Provider</SelectItem>
                    <SelectItem value="distributor">Distributor / Partner</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <Button
                type="submit"
                data-testid="signup-submit-button"
                disabled={isSignupSubmitting || !signupForm.user_type}
                className="w-full bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-semibold py-6"
              >
                {isSignupSubmitting ? "Signing up..." : "Join Pre-Launch List"}
                <ChevronRight className="ml-2 w-5 h-5" />
              </Button>
            </form>
          </div>

          {/* Contact Form */}
          <div className="bg-white rounded-2xl p-6 sm:p-8 md:p-10 border border-indigo-200 shadow-lg">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 rounded-xl bg-indigo-100 flex items-center justify-center">
                <Mail className="w-6 h-6 text-indigo-600" />
              </div>
              <div>
                <h3 className="font-display text-2xl font-bold text-slate-800">Contact Us</h3>
                <p className="text-slate-600 text-sm">We'd love to hear from you</p>
              </div>
            </div>

            <form onSubmit={handleContactSubmit} className="space-y-5">
              <div>
                <Label htmlFor="contact-name" className="text-slate-700">Full Name *</Label>
                <Input
                  id="contact-name"
                  data-testid="contact-name-input"
                  type="text"
                  required
                  value={contactForm.name}
                  onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
                  className="mt-2 bg-slate-100/50 border-slate-300 text-slate-800 focus:border-indigo-500"
                  placeholder="Your full name"
                />
              </div>

              <div>
                <Label htmlFor="contact-email" className="text-slate-700">Email Address *</Label>
                <Input
                  id="contact-email"
                  data-testid="contact-email-input"
                  type="email"
                  required
                  value={contactForm.email}
                  onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                  className="mt-2 bg-slate-100/50 border-slate-300 text-slate-800 focus:border-indigo-500"
                  placeholder="your@email.com"
                />
              </div>

              <div>
                <Label htmlFor="contact-phone" className="text-slate-700">Phone Number</Label>
                <Input
                  id="contact-phone"
                  data-testid="contact-phone-input"
                  type="tel"
                  value={contactForm.phone}
                  onChange={(e) => setContactForm({ ...contactForm, phone: e.target.value })}
                  className="mt-2 bg-slate-100/50 border-slate-300 text-slate-800 focus:border-indigo-500"
                  placeholder="+91 XXXXX XXXXX"
                />
              </div>

              <div>
                <Label htmlFor="contact-message" className="text-slate-700">Message *</Label>
                <Textarea
                  id="contact-message"
                  data-testid="contact-message-input"
                  required
                  rows={4}
                  value={contactForm.message}
                  onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })}
                  className="mt-2 bg-slate-100/50 border-slate-300 text-slate-800 focus:border-indigo-500 resize-none"
                  placeholder="How can we help you?"
                />
              </div>

              <Button
                type="submit"
                data-testid="contact-submit-button"
                disabled={isContactSubmitting}
                className="w-full bg-indigo-500 hover:bg-indigo-400 text-slate-800 font-semibold py-6"
              >
                {isContactSubmitting ? "Sending..." : "Send Message"}
                <Mail className="ml-2 w-5 h-5" />
              </Button>
            </form>

            <div className="mt-8 pt-8 border-t border-slate-200 space-y-4">
              <div className="flex items-start gap-3 text-slate-600">
                <MapPin className="w-5 h-5 text-slate-500 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-slate-800 font-semibold">Ominar Innovations Pvt Ltd</p>
                  <span className="text-sm">100 NCL Innovation Park, Dr Homi Bhabha Road, Pashan, Pune 411008</span>
                </div>
              </div>
              <div className="flex items-center gap-3 text-slate-600">
                <Phone className="w-5 h-5 text-slate-500" />
                <a href="tel:+919075522417" className="text-sm hover:text-slate-800 transition-colors">+91 90755 22417</a>
              </div>
              <a
                href="https://wa.me/919075522417"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="whatsapp-link"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-green-500/10 border border-green-500/30 text-green-600 hover:bg-green-500/20 transition-colors text-sm font-medium"
              >
                Message us on WhatsApp
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
